import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBreadcrumbs } from '../../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { ReportCard, reportsBasePath } from '../../../reports/reportsConfig'

import { DatasetColumnConfig, Insights, toCsvFileName } from '@percent/domain/insights'
import { Anchor, Flex, Grid, Heading, Icon, Text } from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

type ImpactReportProps = {
  report: ReportCard
}

const EMPTY_CELL = '—'

const VISIBLE_FILTERS = ['dateRange', 'organisationCountries', 'programNames', 'mainCauses', 'paidBy']

const toWebsiteHref = (website: string): string | undefined => {
  try {
    const url = new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(website) ? website : `https://${website}`)

    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined
  } catch {
    return undefined
  }
}

const renderWebsite = (value: unknown) => {
  if (typeof value !== 'string' || !value) return EMPTY_CELL

  const href = toWebsiteHref(value)

  return href ? (
    <Anchor href={href} target="_blank" rel="noopener noreferrer">
      {value}
    </Anchor>
  ) : (
    value
  )
}

export function ImpactReport({ report }: Readonly<ImpactReportProps>) {
  const { t } = useTranslation()
  const { insightsService } = useServices()

  const heading = t(report.titleKey)
  const subheading = t(report.descriptionKey)

  useBreadcrumbs([{ label: t('breadcrumb.reports'), path: reportsBasePath('grants') }, { label: heading }])

  const emptyState = useMemo(
    () => (
      <>
        <Icon name="empty" size={6} color="goodstack" />
        <Text size="small">{t('typography.dashboard.noDataToShow')}</Text>
      </>
    ),
    [t]
  )
  const errorLabel = t('typography.dashboard.failedToLoad')

  const datasetColumns = useMemo<DatasetColumnConfig[]>(
    () => [
      { id: 'organisationName', isSticky: true, width: '220px' },
      { id: 'programName', width: '220px' },
      { id: 'mainCause' },
      { id: 'city' },
      { id: 'country' },
      { id: 'amount' },
      { id: 'paidBy' },
      { id: 'paidAt' },
      { id: 'website', render: renderWebsite },
      { id: 'description', width: '320px' }
    ],
    []
  )

  const filterLabels = {
    reset: t('typography.dashboard.filters.reset'),
    all: t('typography.dashboard.filters.all'),
    optionsError: t('typography.dashboard.filters.optionsLoadError'),
    dateRange: {
      label: t('reports.impact.filters.datePaid'),
      custom: t('typography.dashboard.filters.preset.custom'),
      apply: t('typography.dashboard.filters.apply'),
      clear: t('typography.dashboard.filters.clear')
    }
  }

  const exportLabels = {
    trigger: t('reports.export.trigger'),
    title: t('reports.export.title', { report: heading }),
    description: t('reports.export.description'),
    appliedFiltersHeading: t('reports.export.appliedFilters'),
    noFiltersApplied: t('reports.export.noFilters'),
    columnsLabel: t('reports.export.columns'),
    modeDefault: (count: number) => t('reports.export.mode.default', { count }),
    modeAll: (count: number) => t('reports.export.mode.all', { count }),
    modeCustom: t('reports.export.mode.custom'),
    includes: t('reports.export.includes'),
    zeroColumnsError: t('reports.export.zeroColumns'),
    cancel: t('button.cancel'),
    export: t('reports.export.confirm'),
    exportSuccess: (rowCount: number) => t('reports.export.success', { count: rowCount }),
    exportError: t('reports.export.error'),
    exportTooLarge: t('reports.export.tooLarge'),
    exportDateRangeTooLarge: t('reports.export.dateRangeTooLarge'),
    exportUnavailable: t('reports.export.unavailable'),
    close: t('button.close'),
    emptyTitle: t('reports.export.empty.title'),
    emptyFilteredDescription: t('reports.export.empty.filtered'),
    emptyNoDataDescription: t('reports.export.empty.noData')
  }

  const horizontalChart = (metric: string) => (
    <Insights.BarChart
      metric={metric}
      orientation="horizontal"
      yAxisWidth={220}
      emptyState={emptyState}
      errorLabel={errorLabel}
      testId={`report-chart-${metric}`}
    />
  )

  return (
    <Insights.Domain domain={report.domain} service={insightsService}>
      <Flex
        direction="column"
        gap={24}
        w="100%"
        p={24}
        bg="white"
        style={{ borderRadius: 8 }}
        data-testid="report-detail"
      >
        <Flex direction="column" gap="xs">
          <Heading level="h2">{heading}</Heading>
          <Text size="small">{subheading}</Text>
        </Flex>

        <Insights.Filters
          testId="report-filters"
          labels={filterLabels}
          visibleFilters={VISIBLE_FILTERS}
          dropdownAlign="start"
          dateRangeOptions={{ showAllTime: true, labelWhenAllTime: true }}
        />

        <Grid gutter={16}>
          {['total-granted', 'grant-count', 'organisation-count', 'country-count'].map(metric => (
            <Grid.Col key={metric} span={3}>
              <Insights.Stat metric={metric} errorLabel={errorLabel} testId={`report-stat-${metric}`} />
            </Grid.Col>
          ))}
          {['median-grant', 'average-grant', 'largest-grant'].map(metric => (
            <Grid.Col key={metric} span={4}>
              <Insights.Stat metric={metric} errorLabel={errorLabel} testId={`report-stat-${metric}`} />
            </Grid.Col>
          ))}
        </Grid>

        <Flex gap={16}>
          <Insights.LineChart
            metric="granted-by-month"
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-granted-by-month"
          />
          <Insights.LineChart
            metric="grants-by-month"
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-grants-by-month"
          />
        </Flex>

        <Insights.Map
          metric="by-country"
          title={t('reports.impact.map.title')}
          emptyState={emptyState}
          errorLabel={errorLabel}
          testId="report-map-by-country"
        />

        <Flex gap={16}>
          <Insights.BarChart
            metric="by-grant-size"
            emptyState={emptyState}
            errorLabel={errorLabel}
            testId="report-chart-by-grant-size"
          />
          {horizontalChart('by-country')}
        </Flex>

        <Flex gap={16}>
          {horizontalChart('by-program')}
          {horizontalChart('by-cause')}
        </Flex>

        {horizontalChart('by-organisation')}

        <Insights.Dataset
          dataset="grants"
          columns={datasetColumns}
          title={t('reports.impact.table.title')}
          action={
            <Insights.Export
              dataset="grants"
              defaultColumns={datasetColumns}
              fileName={toCsvFileName(heading)}
              labels={exportLabels}
              testId="report-export"
            />
          }
          pageSizeSelector={{ options: [10, 20, 50], label: t('typography.resultsOnPage') }}
          labels={{
            empty: t('reports.impact.table.empty'),
            error: errorLabel,
            results: count => t('reports.impact.table.results', { count })
          }}
          testId="report-dataset"
        />
      </Flex>
    </Insights.Domain>
  )
}
